import React, { useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Separator,
  Button,
} from '@lb-classroom/ui';
import ModalMessage from '../../UI/organisms/ModalMessage';
import LoadingMessage from '../../UI/organisms/AppLoadingMessage';
import moneyUtils from '../../../utils/moneyUtils';
import * as PaymentActions from '../../../actions/payment';
import * as StripeActions from '../../../actions/stripeMethod';
import { i18n, withTranslation } from 'next-i18next';
import appUtils from '../../../utils/appUtils';
import { X } from 'lucide-react';

interface PlanTransferModalContentProps {
  t: (key: string, options?: Record<string, unknown>) => string;
  mutating: boolean;
  stripeCard?: any;
  plan: any;
  payment: any;
  hideModal: () => void;
  paymentActions: any;
  stripeActions: any;
  message: string;
  messageType: string;
  price?: any;
}

function mapStateToProps(state: Record<string, any>) {
  const { payment, paymentMethod } = state;
  return {
    loadingCard: paymentMethod.loading,
    mutating: payment.mutatingSubscription,
    stripeCard: paymentMethod.card,
  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    paymentActions: bindActionCreators<any, any>(PaymentActions, dispatch),
    stripeActions: bindActionCreators<any, any>(StripeActions, dispatch),
  };
}

const PlanTransferModalContent = (props: PlanTransferModalContentProps) => {
  const {
    stripeCard,
    stripeActions,
    mutating,
    hideModal,
    plan,
    payment,
    paymentActions,
    t,
    message,
    messageType,
    price,
  } = props;

  useEffect(() => {
    if (!stripeCard) {
      stripeActions.fetchCard();
    }
  }, [stripeCard, stripeActions]);

  const handleHideModal = (e) => {
    e.preventDefault();
    if (!mutating) hideModal();
  };

  const handleTransfer = () => {
    if (stripeCard) {
      const currency = moneyUtils.stripeCountryToCurrency(stripeCard.country);
      const newPrice = moneyUtils.getPrice(plan.prices, currency);
      paymentActions.transfer(payment.id, newPrice.id);
    }
  };

  const currentLocale = appUtils.getLocaleFromLang(i18n.language);
  const loadingMessage = mutating ? t('general.processing') : null;
  const loadingClass = 'modal-loading-message';
  let currency = price.currency_code;
  let newPrice = price;
  if (stripeCard) {
    currency = moneyUtils.stripeCountryToCurrency(stripeCard.country);
    newPrice = moneyUtils.getPrice(plan.prices, currency);
  }
  let currencyCode = moneyUtils.currencyToCode(currency);
  if (currentLocale === 'pt') currencyCode = 'US$';
  const amount = newPrice.amount;
  return (
    <div className="max-w-sm mx-auto">
      <Card className="z-50">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>{t('student.billing.planTransfer.title')}</CardTitle>
          <button type="button" className="modal-close" onClick={hideModal}>
            <span className="sr-only">Close</span>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </CardHeader>
        <CardContent>
          <LoadingMessage
            message={loadingMessage}
            loadingClass={loadingClass}
          />
          <ModalMessage message={message} messageType={messageType} />
          <div>
            <div className="mb-4 checkout-review-explanation">
              {t('student.billing.planTransfer.intro', {
                monthlyCharge: currencyCode + amount.toString(),
              })}
            </div>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-8">
                <div className="grid grid-cols-12 gap-4">
                  <div className="checkout-review-plan-name col-span-12">
                    <div className="float-left">
                      <strong>
                        {t('student.billing.planTransfer.newPlan')}
                      </strong>
                    </div>
                    <div className="float-right">{plan.name}</div>
                  </div>
                </div>
                <Separator className="my-4" />
                <div className="grid grid-cols-12 gap-4">
                  <div className="checkout-review-plan-name col-span-12">
                    <div className="float-left">
                      <strong>
                        {t('student.billing.planTransfer.newMonthlyCharge')}
                      </strong>
                    </div>
                    <div className="float-right">
                      {currencyCode}
                      {amount}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Separator className="my-4" />
          <div className="control-group text-right">
            <Button
              variant="outline"
              disabled={mutating}
              onClick={handleHideModal}
            >
              {t('links.cancel')}
            </Button>
            <Button onClick={handleTransfer} type="submit" disabled={mutating}>
              {t('links.transfer')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const wrapped = withTranslation('common')(PlanTransferModalContent);

export default connect(mapStateToProps, mapDispatchToProps)(wrapped);
